/**
 * Performance Table Component
 * Detailed sortable data table with export functionality
 * Phase 6.4 - Performance Table
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  Menu,
  MenuItem,
  Skeleton,
  LinearProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as NeutralIcon,
  Person as PersonIcon
} from '@mui/icons-material';

export interface SalesRepPerformanceData {
  id: string;
  name: string;
  avatar?: string;
  leadsAssigned: number;
  leadsConverted: number;
  conversionRate: number;
  revenue: number;
  avgDealSize: number;
  avgDealCycle: number;
  activitiesCompleted: number;
  tasksOverdue: number;
  responseTime: number; // hours
  trend: 'up' | 'down' | 'stable';
  trendValue: number;
}

export interface PerformanceTableProps {
  data: SalesRepPerformanceData[];
  loading?: boolean;
  title?: string;
}

type SortDirection = 'asc' | 'desc';
type SortField = keyof SalesRepPerformanceData;

const PerformanceTable: React.FC<PerformanceTableProps> = ({
  data,
  loading = false,
  title = 'Detailed Performance Report'
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState<SortField>('revenue');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Filter and sort data
  const processedData = useMemo(() => {
    let filtered = data;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = data.filter(row =>
        row.name.toLowerCase().includes(term)
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return sorted;
  }, [data, searchTerm, sortField, sortDirection]);

  // Paginated data
  const paginatedData = processedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-HK', {
      style: 'currency',
      currency: 'HKD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Get trend icon
  const getTrendIcon = (trend: 'up' | 'down' | 'stable', value: number) => {
    switch (trend) {
      case 'up':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
            <TrendingUpIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" fontWeight={600}>+{value}%</Typography>
          </Box>
        );
      case 'down':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main' }}>
            <TrendingDownIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" fontWeight={600}>-{Math.abs(value)}%</Typography>
          </Box>
        );
      default:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
            <NeutralIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">0%</Typography>
          </Box>
        );
    }
  };

  // Get conversion rate color
  const getConversionColor = (rate: number) => {
    if (rate >= 25) return 'success';
    if (rate >= 15) return 'info';
    if (rate >= 10) return 'warning';
    return 'error';
  };

  // Export functions
  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const exportToCSV = () => {
    const headers = [
      'Name',
      'Leads Assigned',
      'Leads Converted',
      'Conversion Rate (%)',
      'Revenue (HKD)',
      'Avg Deal Size (HKD)',
      'Avg Deal Cycle (Days)',
      'Activities Completed',
      'Tasks Overdue',
      'Avg Response Time (Hours)',
      'Trend',
      'Trend Value (%)'
    ];

    const rows = processedData.map(row => [
      row.name,
      row.leadsAssigned,
      row.leadsConverted,
      row.conversionRate,
      row.revenue,
      row.avgDealSize,
      row.avgDealCycle,
      row.activitiesCompleted,
      row.tasksOverdue,
      row.responseTime,
      row.trend,
      row.trendValue
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `performance_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleExportClose();
  };

  const exportToJSON = () => {
    const jsonContent = JSON.stringify(processedData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `performance_report_${new Date().toISOString().split('T')[0]}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleExportClose();
  };

  // Column definitions
  const columns = [
    { id: 'name' as SortField, label: 'Sales Rep', minWidth: 180 },
    { id: 'leadsAssigned' as SortField, label: 'Leads', minWidth: 80, align: 'right' as const },
    { id: 'leadsConverted' as SortField, label: 'Converted', minWidth: 90, align: 'right' as const },
    { id: 'conversionRate' as SortField, label: 'Conv. Rate', minWidth: 100, align: 'right' as const },
    { id: 'revenue' as SortField, label: 'Revenue', minWidth: 120, align: 'right' as const },
    { id: 'avgDealSize' as SortField, label: 'Avg Deal', minWidth: 110, align: 'right' as const },
    { id: 'avgDealCycle' as SortField, label: 'Cycle (Days)', minWidth: 100, align: 'right' as const },
    { id: 'activitiesCompleted' as SortField, label: 'Activities', minWidth: 90, align: 'right' as const },
    { id: 'responseTime' as SortField, label: 'Response', minWidth: 90, align: 'right' as const },
    { id: 'trend' as SortField, label: 'Trend', minWidth: 100, align: 'center' as const }
  ];

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight="600">{title}</Typography>
        </Box>
        {[1, 2, 3, 4, 5].map(i => (
          <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" fontWeight="600">{title}</Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ width: 200 }}
          />

          {/* Export Button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportClick}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={handleExportClose}
          >
            <MenuItem onClick={exportToCSV}>Export as CSV</MenuItem>
            <MenuItem onClick={exportToJSON}>Export as JSON</MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  sx={{ fontWeight: 600, bgcolor: 'grey.100' }}
                >
                  <TableSortLabel
                    active={sortField === column.id}
                    direction={sortField === column.id ? sortDirection : 'asc'}
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                  cursor: 'pointer'
                }}
              >
                {/* Name with Avatar */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      src={row.avatar}
                      sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
                    >
                      {row.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {row.name}
                      </Typography>
                      {row.tasksOverdue > 0 && (
                        <Typography variant="caption" color="error.main">
                          {row.tasksOverdue} overdue
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>

                {/* Leads Assigned */}
                <TableCell align="right">
                  <Typography variant="body2">{row.leadsAssigned}</Typography>
                </TableCell>

                {/* Leads Converted */}
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={500}>{row.leadsConverted}</Typography>
                </TableCell>

                {/* Conversion Rate */}
                <TableCell align="right">
                  <Chip
                    label={`${row.conversionRate.toFixed(1)}%`}
                    size="small"
                    color={getConversionColor(row.conversionRate)}
                    sx={{ fontWeight: 600, minWidth: 60 }}
                  />
                </TableCell>

                {/* Revenue */}
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    {formatCurrency(row.revenue)}
                  </Typography>
                </TableCell>

                {/* Avg Deal Size */}
                <TableCell align="right">
                  <Typography variant="body2">
                    {formatCurrency(row.avgDealSize)}
                  </Typography>
                </TableCell>

                {/* Avg Deal Cycle */}
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    color={row.avgDealCycle <= 30 ? 'success.main' : row.avgDealCycle <= 45 ? 'text.primary' : 'error.main'}
                  >
                    {row.avgDealCycle}
                  </Typography>
                </TableCell>

                {/* Activities */}
                <TableCell align="right">
                  <Typography variant="body2">{row.activitiesCompleted}</Typography>
                </TableCell>

                {/* Response Time */}
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    color={row.responseTime <= 2 ? 'success.main' : row.responseTime <= 4 ? 'text.primary' : 'error.main'}
                  >
                    {row.responseTime}h
                  </Typography>
                </TableCell>

                {/* Trend */}
                <TableCell align="center">
                  {getTrendIcon(row.trend, row.trendValue)}
                </TableCell>
              </TableRow>
            ))}

            {paginatedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No data found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={processedData.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      {/* Summary Row */}
      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Team Summary
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
            <Typography variant="body1" fontWeight={600} color="success.main">
              {formatCurrency(data.reduce((sum, row) => sum + row.revenue, 0))}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Total Conversions</Typography>
            <Typography variant="body1" fontWeight={600}>
              {data.reduce((sum, row) => sum + row.leadsConverted, 0)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Avg Conversion Rate</Typography>
            <Typography variant="body1" fontWeight={600}>
              {(data.reduce((sum, row) => sum + row.conversionRate, 0) / data.length).toFixed(1)}%
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Avg Deal Cycle</Typography>
            <Typography variant="body1" fontWeight={600}>
              {Math.round(data.reduce((sum, row) => sum + row.avgDealCycle, 0) / data.length)} days
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PerformanceTable;
